import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api, type StudentDto } from "../lib/api";
import { Button, Card, Field, Input } from "./ui";

type Choice = "" | "yes" | "no" | "veg" | "non-veg" | "both";
interface Option { value: Exclude<Choice, "">; label: string }
const YES_NO: Option[] = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }];
const DIETARY: Option[] = [
  { value: "veg", label: "Vegetarian" },
  { value: "non-veg", label: "Non-vegetarian" },
  { value: "both", label: "Both" },
];

function ChoiceToggle({ label, value, options, onChange }: { label: string; value: Choice; options: Option[]; onChange: (v: Exclude<Choice, "">) => void }) {
  return <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
    {options.map((opt) => <button key={opt.value} type="button" role="radio" aria-checked={value === opt.value} onClick={() => onChange(opt.value)} className={`flex-1 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#AFDDFF]/50 ${value === opt.value ? "border-[#AFDDFF] bg-[#AFDDFF]/15 text-white" : "border-white/15 bg-white/[0.02] text-white/70 hover:border-white/35 hover:text-white"}`}>{opt.label}</button>)}
  </div>;
}

export default function StudentProfileForm({ student, onSaved }: { student: StudentDto; onSaved: (student: StudentDto) => void }) {
  const [enrollmentNumber, setEnrollmentNumber] = useState(student.enrollmentNumber ?? "");
  const [semester, setSemester] = useState(student.semester ?? "");
  const [dietary, setDietary] = useState<Choice>((student.dietary as Choice) ?? "");
  const [hasTransport, setHasTransport] = useState<Choice>(student.hasTransport == null ? "" : student.hasTransport ? "yes" : "no");
  const [canPickupFriends, setCanPickupFriends] = useState<Choice>(student.canPickupFriends == null ? "" : student.canPickupFriends ? "yes" : "no");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!hasTransport || !dietary || !semester.trim() || !enrollmentNumber.trim()) {
      setError("Please complete your enrollment number, semester, food preference, and transport answer.");
      return;
    }
    if (hasTransport === "yes" && !canPickupFriends) {
      setError("Please say whether you can pick up or drop friends.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.studentProfile({ enrollmentNumber, semester, dietary: dietary as "veg" | "non-veg" | "both", hasTransport: hasTransport === "yes", canPickupFriends: hasTransport === "yes" ? canPickupFriends === "yes" : null });
      onSaved(res.student);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We could not save your profile. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return <Card className="p-6 md:p-8">
    <h3 className="text-xl font-semibold text-white">Complete your student profile</h3>
    <p className="mt-1 text-sm text-white/55">One-time details for eligibility, food planning, and transport arrangements.</p>
    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Enrollment number" htmlFor="profile-enrollment"><Input id="profile-enrollment" value={enrollmentNumber} onChange={(e) => setEnrollmentNumber(e.target.value)} autoComplete="off" /></Field>
      <Field label="Semester" htmlFor="profile-semester"><Input id="profile-semester" value={semester} onChange={(e) => setSemester(e.target.value)} placeholder="e.g. 1st / 3rd" /></Field>
      <Field label="Food preference" htmlFor="profile-diet"><ChoiceToggle label="Food preference" value={dietary} options={DIETARY} onChange={setDietary} /></Field>
      <Field label="Do you have your own means of transport?" htmlFor="profile-transport"><ChoiceToggle label="Do you have your own means of transport?" value={hasTransport} options={YES_NO} onChange={(v) => { setHasTransport(v); if (v === "no") setCanPickupFriends(""); }} /></Field>
      {hasTransport === "yes" && <Field label="Can you pick up / drop your friends?" htmlFor="profile-pickup"><ChoiceToggle label="Can you pick up or drop your friends?" value={canPickupFriends} options={YES_NO} onChange={setCanPickupFriends} /></Field>}
    </div>
    {error && <p className="mt-4 flex items-start gap-2 text-sm text-amber-300"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{error}</p>}
    <div className="mt-6"><Button onClick={() => void submit()} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}{busy ? "Saving profile…" : "Save profile & continue to polls"}</Button></div>
    <p className="mt-4 text-xs text-white/45">Enrollment, semester, dietary and transport details are used for event planning and shown only to the organizing committee.</p>
  </Card>;
}
